import {
    Box,
    Flex,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    Spinner,
    Text,
    VStack
} from '@chakra-ui/react'
import { FiChevronLeft, FiChevronRight, FiSearch } from 'react-icons/fi'
import { RegistryParent, getParentName } from '../types'

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200]

type Props = {
    parents: RegistryParent[]
    total: number
    search: string
    onSearch: (value: string) => void
    selectedId: string | null
    onSelect: (id: string) => void
    page: number
    pageCount: number
    onPageChange: (page: number) => void
    pageSize: number
    onPageSizeChange: (size: number) => void
    loading?: boolean
}

export default function ParentList({
    parents,
    total,
    search,
    onSearch,
    selectedId,
    onSelect,
    page,
    pageCount,
    onPageChange,
    pageSize,
    onPageSizeChange,
    loading
}: Props) {
    return (
        <Flex direction="column" h="full">
            <Box p={3} borderBottomWidth="1px" borderColor="gray.100">
                <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none">
                        <FiSearch color="gray" />
                    </InputLeftElement>
                    <Input
                        placeholder="Search parents..."
                        value={search}
                        onChange={(event) => onSearch(event.target.value)}
                        borderRadius="md"
                    />
                </InputGroup>
                <Flex align="center" gap={2} mt={2}>
                    <Text fontSize="xs" color="gray.500">
                        {total} {total === 1 ? 'parent' : 'parents'}
                    </Text>
                    {loading ? <Spinner size="xs" color="gray.400" /> : null}
                </Flex>
            </Box>
            <VStack align="stretch" spacing={0} overflowY="auto" flex="1">
                {parents.length === 0 ? (
                    <Text p={4} fontSize="sm" color="gray.500">
                        No parents found.
                    </Text>
                ) : (
                    parents.map((parent) => {
                        const isActive = parent._id === selectedId
                        const childCount = parent.children?.length ?? 0
                        return (
                            <Box
                                as="button"
                                key={parent._id}
                                textAlign="left"
                                px={4}
                                py={3}
                                borderBottomWidth="1px"
                                borderColor="gray.50"
                                bg={isActive ? 'brand.50' : 'white'}
                                _hover={{ bg: isActive ? 'brand.50' : 'gray.50' }}
                                onClick={() => onSelect(parent._id)}
                            >
                                <Text fontWeight="medium" fontSize="sm" noOfLines={1}>
                                    {getParentName(parent)}
                                </Text>
                                <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                    {parent.branch?.name || 'No branch'} · {childCount}{' '}
                                    {childCount === 1 ? 'child' : 'children'}
                                </Text>
                            </Box>
                        )
                    })
                )}
            </VStack>
            <Flex
                align="center"
                justify="space-between"
                gap={2}
                px={2}
                py={2}
                borderTopWidth="1px"
                borderColor="gray.100"
            >
                <Select
                    size="xs"
                    value={pageSize}
                    onChange={(event) => onPageSizeChange(Number(event.target.value))}
                    w="auto"
                    borderRadius="md"
                    aria-label="Rows per page"
                >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                        <option key={size} value={size}>
                            {size} / page
                        </option>
                    ))}
                </Select>
                {pageCount > 1 ? (
                    <Flex align="center" gap={1}>
                        <IconButton
                            aria-label="Previous page"
                            icon={<FiChevronLeft />}
                            size="xs"
                            variant="ghost"
                            onClick={() => onPageChange(page - 1)}
                            isDisabled={page <= 1}
                        />
                        <Text fontSize="xs" color="gray.500">
                            Page {page} of {pageCount}
                        </Text>
                        <IconButton
                            aria-label="Next page"
                            icon={<FiChevronRight />}
                            size="xs"
                            variant="ghost"
                            onClick={() => onPageChange(page + 1)}
                            isDisabled={page >= pageCount}
                        />
                    </Flex>
                ) : null}
            </Flex>
        </Flex>
    )
}
